import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Loader2, HardDrive } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { showConfirmDialog } from "../../../components/ConfirmDialog";

const SMOOTH_ALPHA = 0.12;
const SMOOTH_SNAP = 0.06;

/**
 * Modal xác nhận tải (header đỏ, thông tin, nút Bắt đầu tải, progress bar).
 * @param {boolean} [localVersionReady] — đã có ZIP đúng phiên bản trên thiết bị → xác nhận trước khi tải lại
 * @param {(opts: { onProgress?: (p: { loaded: number; total: number }) => void }) => Promise<void>} onStartDownload
 */
const LessonDownloadModal = ({
  isOpen,
  onClose,
  title = "Tải tài liệu",
  courseName = "—",
  lessonTitle = "—",
  contentLine = "",
  typeLabel = "—",
  typeHighlight = false,
  fileSize = "—",
  localVersionReady = false,
  onStartDownload,
}) => {
  const [loading, setLoading] = useState(false);
  const [progressIndeterminate, setProgressIndeterminate] = useState(false);
  /** Tiến độ hiển thị (0–100), nội suy mượt từ giá trị mạng */
  const [smoothPct, setSmoothPct] = useState(0);

  const targetPctRef = useRef(0);
  const smoothValRef = useRef(0);
  const rafRef = useRef(null);
  const lastPaintTimeRef = useRef(0);
  const lastEmittedValRef = useRef(0);

  const stopSmoothLoop = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    lastPaintTimeRef.current = 0;
    lastEmittedValRef.current = 0;
  }, []);

  const resetProgress = useCallback(() => {
    stopSmoothLoop();
    targetPctRef.current = 0;
    smoothValRef.current = 0;
    lastEmittedValRef.current = 0;
    setSmoothPct(0);
  }, [stopSmoothLoop]);

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      setProgressIndeterminate(false);
      resetProgress();
    }
  }, [isOpen, resetProgress]);

  useEffect(() => {
    if (!loading || progressIndeterminate) {
      stopSmoothLoop();
      return;
    }

    const tick = (now) => {
      const target = targetPctRef.current;
      let s = smoothValRef.current;
      const diff = target - s;
      if (Math.abs(diff) < SMOOTH_SNAP) {
        s = target;
      } else {
        const alpha = Math.min(0.42, SMOOTH_ALPHA + Math.abs(diff) / 650);
        s += diff * alpha;
      }
      smoothValRef.current = s;

      const since = now - lastPaintTimeRef.current;
      const shouldPaint =
        since >= 20 ||
        Math.abs(s - lastEmittedValRef.current) >= 0.4 ||
        s >= 99.9 ||
        s <= 0.02;
      if (shouldPaint) {
        lastPaintTimeRef.current = now;
        lastEmittedValRef.current = s;
        setSmoothPct(s);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => stopSmoothLoop();
  }, [loading, progressIndeterminate, stopSmoothLoop]);

  const applyProgress = ({ loaded, total }) => {
    const t = total ?? 0;
    const l = loaded ?? 0;
    if (t > 0) {
      setProgressIndeterminate(false);
      targetPctRef.current = Math.min(100, (l / t) * 100);
    } else if (l > 0) {
      setProgressIndeterminate(true);
    }
  };

  const handleStart = async () => {
    if (!onStartDownload) return;

    if (localVersionReady) {
      const confirm = await showConfirmDialog({
        title: "Tải lại bài giảng?",
        text:
          "Thiết bị đã có bản bài giảng đúng phiên bản hiện tại. Tải lại có thể tốn thêm lượt tải/xem (nếu tài khoản có giới hạn). Bạn chắc chắn muốn tải lại?",
        confirmText: "Vẫn tải lại",
        cancelText: "Hủy",
      });
      if (!confirm.isConfirmed) return;
    }

    resetProgress();
    setProgressIndeterminate(false);
    setLoading(true);
    try {
      const ok = await onStartDownload({
        onProgress: applyProgress,
      });
      if (ok) {
        setProgressIndeterminate(false);
        targetPctRef.current = 100;
        smoothValRef.current = 100;
        lastEmittedValRef.current = 100;
        setSmoothPct(100);
        await new Promise((r) => setTimeout(r, 160));
        onClose?.();
      } else {
        resetProgress();
        setProgressIndeterminate(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[220] bg-black/50"
            onClick={() => !loading && onClose?.()}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[230] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-red-600 px-5 py-3 text-white">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-black">
                    IMA
                  </div>
                  <span className="text-sm font-bold tracking-wide truncate">VIỆT NAM</span>
                </div>
                <button
                  type="button"
                  disabled={loading}
                  onClick={onClose}
                  className="rounded-lg p-1.5 hover:bg-white/10 disabled:opacity-50"
                  aria-label="Đóng"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 pt-6 pb-2">
                <h3 className="text-center text-lg font-black text-gray-900">{title}</h3>
              </div>

              {localVersionReady ? (
                <div className="mx-6 mb-1 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                  <HardDrive className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={2} />
                  <p>
                    <span className="font-bold">Đã có trên thiết bị</span> — phiên bản khớp với máy chủ. Chỉ bấm «Bắt đầu
                    tải» nếu bạn muốn tải lại có chủ đích.
                  </p>
                </div>
              ) : null}

              <div className="space-y-3 px-6 py-4 text-sm">
                <Row label="Khóa học" value={courseName} />
                <Row label="Bài học" value={lessonTitle} />
                <Row label="Nội dung" value={contentLine || "—"} />
                <div className="flex gap-2 border-b border-gray-100 py-2">
                  <span className="w-28 shrink-0 text-gray-500">Phân loại</span>
                  <span
                    className={`min-w-0 flex-1 font-semibold ${
                      typeHighlight ? "text-red-600" : "text-gray-900"
                    }`}
                  >
                    {typeLabel}
                  </span>
                </div>
                <Row label="Dung lượng" value={fileSize} />
              </div>

              <div className="border-t border-gray-100 px-6 py-4 space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
                      {progressIndeterminate && loading ? (
                        <motion.div
                          className="h-full w-[40%] rounded-full bg-gradient-to-r from-red-500 via-red-600 to-red-500"
                          animate={{ x: ["-30%", "190%"] }}
                          transition={{
                            duration: 1.35,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                          style={{ willChange: "transform" }}
                        />
                      ) : (
                        <div
                          className="h-full origin-left rounded-full bg-gradient-to-r from-red-600 to-red-700 shadow-sm shadow-red-900/25"
                          style={{
                            width: "100%",
                            transform: `scaleX(${
                              loading ? Math.max(0.003, smoothPct / 100) : 0
                            })`,
                            willChange: "transform",
                          }}
                        />
                      )}
                    </div>
                    <p className="text-[11px] font-mono text-gray-600 tabular-nums">
                      {loading
                        ? progressIndeterminate
                          ? "Đang tải…"
                          : `${Math.min(100, Math.round(smoothPct))}%`
                        : "Sẵn sàng"}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleStart}
                    className="shrink-0 rounded-xl bg-red-600 px-6 py-2.5 text-xs font-black uppercase tracking-widest text-white shadow-md shadow-red-200 hover:bg-red-700 disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Đang tải…
                      </span>
                    ) : (
                      "Bắt đầu tải"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

function Row({ label, value }) {
  return (
    <div className="flex gap-2 border-b border-gray-100 py-2">
      <span className="w-28 shrink-0 text-gray-500">{label}</span>
      <span className="min-w-0 flex-1 font-semibold text-gray-900">{value}</span>
    </div>
  );
}

export default LessonDownloadModal;
